import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api/client';
import { invoiceEndpoints } from '@/lib/api/endpoints';
import { invoiceKeys } from '@/lib/queryKeys';
import type { JobDocumentDiscount } from '@/lib/sales/invoice-member-pricing';
import type { InvoiceLineItem } from '@/types/invoice';

/** One job line the new invoice may bill, and whether one already does. */
export interface BillableJobLine {
  key: string;
  name: string;
  quantity: number;
  lineTotal: number;
  sourceEstimateId: string | null;
  sourceEstimateNumber: string | null;
  /** The invoice number already billing this line, or null if it is free. */
  invoicedOn: string | null;
}

export interface InvoiceCreatePreview {
  availableDeposit: number;
  availableCredit: number;
  unbilledVisitExtras: Array<Omit<InvoiceLineItem, 'id'>>;
  unbilledVisitAppointmentIds: string[];
  /** Absent on a tenant whose financials are masked. */
  billableLines?: BillableJobLine[];
  /**
   * The job's own lines as this invoice would bill them, tax included. The
   * job's line items carry no tax rate, so a draft built from them alone
   * totalled pre-tax. Null when the server could not resolve the tax.
   */
  jobLinesPriced?: Array<Omit<InvoiceLineItem, 'id'>> | null;
  /**
   * The job's document-level discount as a RULE. A flat discount is one amount
   * shared across the lines an invoice bills, so the form re-spreads it over
   * what it drafts rather than keeping the preview's own allocation.
   */
  documentDiscount?: { type: 'PERCENT' | 'FLAT'; value: number } | null;
  /**
   * The JOB's document discount with each job line's share of it, when this
   * invoice carries the job's discount. An invoice billing only some of the
   * job's lines takes exactly their shares, so a second invoice for the rest
   * never takes the whole discount again. Null when the job discounts nothing,
   * or on an existing invoice whose discount is its own.
   */
  jobDocumentDiscount?: JobDocumentDiscount | null;
}

export function useInvoiceCreatePreview(jobId: string, enabled = true, invoiceId?: string) {
  return useApiQuery<InvoiceCreatePreview>({
    queryKey: [...invoiceKeys.createPreview(jobId), invoiceId ?? 'new'],
    queryFn: () =>
      apiGet<InvoiceCreatePreview>(
        operationsClient,
        invoiceId ? `/invoices/${invoiceId}/billable-lines` : invoiceEndpoints.createPreview(jobId),
      ),
    enabled: enabled && Boolean(jobId),
    showErrorToast: false,
  });
}
